import React, { FunctionComponent } from 'react';

import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

export enum Period {
    Today = 'today',
    Yesterday = 'yesterday',
    ThisWeek = 'this_week',
    PreviousWeek = 'previous_week',
    RollingWeek = 'rolling_week',
    ThisMonth = 'this_month',
    PreviousMonth = 'previous_month',
    Previous2Months = 'previous_2_months',
    Previous3Months = 'previous_3_months',
    Previous6Months = 'previous_6_months',
    Previous12Months = 'previous_12_months',
    Current3Months = 'current_3_months',
    ThisQuarter = 'this_quarter',
    PreviousQuarter = 'previous_quarter',
    ThisYear = 'this_year',
    PreviousYear = 'previous_year',
    Previous2Years = 'previous_2_years',
    Previous3Years = 'previous_3_years',
    YearToDate = 'year_to_date',
    LifeToDate = '', // All Time
    Last7Days = 'day_last_7_days',
    Last30Days = 'day_last_30_days',
    Last60Days = 'day_last_60_days',
    Last90Days = 'day_last_90_days',
    Last6Weeks = 'last_6_weeks', // TODO - change to 'week_last_6_weeks'
    Last6Months = 'month_last_6_months',
    Last1Year = 'month_last_1_year',
    Last2Years = 'month_last_2_years',
    Last3Years = 'month_last_3_years',
    Ago2Years = 'ago_2_years',
    Ago3Years = 'ago_3_years',
    Custom = 'custom_dates',
    FiscalCurrentWeek = 'fiscal_current_week',
    FiscalPreviousWeek = 'fiscal_previous_week',
    FiscalCurrentMonth = 'fiscal_current_month',
    FiscalPreviousMonth = 'fiscal_previous_month',
    FiscalCurrentQuarter = 'fiscal_current_quarter',
    FiscalPreviousQuarter = 'fiscal_previous_quarter',
    FiscalCurrentYear = 'fiscal_current_year',
    FiscalPreviousYear = 'fiscal_previous_year',
    FiscalAgo2Years = 'fiscal_ago_2_years',
    FiscalAgo3Years = 'fiscal_ago_3_years',
}

export enum Frequency {
    Daily = 1,
    Weekly = 2,
    Monthly = 3,
    Quarterly = 4,
    Yearly = 5,
    Immediate = 6,
}

export enum Group {
    Day = 1,
    Week = 2,
    Month = 3,
    Quarter = 4,
    Year = 5,
    DayOfWeek = 6,
    TimeOfDay = 7,
    FiscalWeek = 8,
    FiscalMonth = 9,
    FiscalQuarter = 10,
    FiscalYear = 11,
}

export const CUSTOM_DATE_DELIMITER = '$$';
export const CUSTOM_DATE_TIME_FORMAT = 'MM/dd/yyyy HH:mm:ss';
export const CUSTOM_DATE_TIME_TZ_FORMAT = 'MMM dd yyyy hh:mm a ZZZZ';
export const PST_TIMEZONE = 'America/Los_Angeles';

export interface DateRange {
    startDate: Date;
    endDate: Date;
    label: string;
    value: string;
}

export enum MaxUnit {
    seconds = 'seconds',
    minutes = 'minutes',
    hours = 'hours',
    days = 'days',
    weeks = 'weeks',
    months = 'months',
}
interface Props {
    dateTime: number;
    maxUnit?: 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'none';
    addTimeAgo?: boolean;
}

export const RelativeDateTime: FunctionComponent<Props> = ({ dateTime, maxUnit = 'none', addTimeAgo }) => {
    const { t } = useTranslation('core');
    const date = DateTime.fromMillis(dateTime).toLocaleString(DateTime.DATE_SHORT);
    let relativeDateTime;

    const duration = DateTime.fromMillis(Date.now()).diff(DateTime.fromMillis(dateTime), [
        'seconds',
        'minutes',
        'hours',
        'days',
        'weeks',
        'months',
        'years',
    ]);

    if (duration.as('seconds') < 60) {
        relativeDateTime = t('RELATIVE_DATE_TIME_NOW');
    } else if (maxUnit === 'seconds') {
        relativeDateTime = date;
    } else if (duration.as('minutes') < 60) {
        relativeDateTime = addTimeAgo
            ? t('RELATIVE_DATE_TIME_MINUTE_AGO', { count: Math.floor(duration.as('minutes')) })
            : t('RELATIVE_DATE_TIME_MINUTE', { count: Math.floor(duration.as('minutes')) });
    } else if (maxUnit === 'minutes') {
        relativeDateTime = date;
    } else if (duration.as('hours') < 24) {
        relativeDateTime = addTimeAgo
            ? t('RELATIVE_DATE_TIME_HOUR_AGO', { count: Math.floor(duration.as('hours')) })
            : t('RELATIVE_DATE_TIME_HOUR', { count: Math.floor(duration.as('hours')) });
    } else if (maxUnit === 'hours') {
        relativeDateTime = date;
    } else if (duration.as('days') <= 7) {
        relativeDateTime = addTimeAgo
            ? t('RELATIVE_DATE_TIME_DAY_AGO', { count: Math.floor(duration.as('days')) })
            : t('RELATIVE_DATE_TIME_DAY', { count: Math.floor(duration.as('days')) });
    } else if (maxUnit === 'days') {
        relativeDateTime = date;
    } else if (duration.as('weeks') <= 4) {
        relativeDateTime = addTimeAgo
            ? t('RELATIVE_DATE_TIME_WEEK_AGO', { count: Math.floor(duration.as('weeks')) })
            : t('RELATIVE_DATE_TIME_WEEK', { count: Math.floor(duration.as('weeks')) });
    } else if (maxUnit === 'weeks') {
        relativeDateTime = date;
    } else if (duration.as('months') <= 12) {
        relativeDateTime = addTimeAgo
            ? t('RELATIVE_DATE_TIME_MONTH_AGO', { count: Math.floor(duration.as('months')) })
            : t('RELATIVE_DATE_TIME_MONTH', { count: Math.floor(duration.as('months')) });
    } else if (maxUnit === 'months') {
        relativeDateTime = date;
    } else {
        relativeDateTime = addTimeAgo
            ? t('RELATIVE_DATE_TIME_YEAR_AGO', { count: Math.floor(duration.as('years')) })
            : t('RELATIVE_DATE_TIME_YEAR', { count: Math.floor(duration.as('years')) });
    }

    return <>{relativeDateTime}</>;
};

export const getDateZone = (zone: string): string => {
    return DateTime.fromMillis(Date.now(), { zone }).toFormat('ZZZZ');
};

export const milliSecondsToDHMS = (milliSeconds: number): string => {
    if (!milliSeconds) {
        return '';
    }
    const result = [];
    const seconds = Math.floor(milliSeconds / 1000);
    const days = Math.floor(seconds / (60 * 60 * 24));
    let hr = Math.floor(seconds / (60 * 60)) % 24;
    let min = Math.floor(seconds / 60) % 60;
    const sec = seconds % 60;

    if (days) {
        result.push(`${days}d`);
        if (hr) {
            result.push(`${hr}h`);
        }
    } else if (hr) {
        result.push(`${hr}h`);
        if (min) {
            result.push(`${min}m`);
        }
    } else if (min) {
        if (sec > 30) {
            min += 1;
        }
        if (min === 60) {
            hr += 1;
            result.push(`${hr}h`);
        } else {
            result.push(`${min}m`);
        }
    } else {
        result.push(`${sec}s`);
    }
    return result.join(' ');
};

export const getDateRangeDiff = (dateTime: number, maxUnit: MaxUnit) => {
    const duration = DateTime.fromMillis(Date.now()).diff(DateTime.fromMillis(dateTime), [
        'seconds',
        'minutes',
        'hours',
        'days',
        'weeks',
        'months',
        'years',
    ]);

    return duration.as(maxUnit);
};
